import { useEffect } from "react";
import { trackEvent } from "./mixpanel";
import type { AnalyticsEventName } from "./events";

/**
 * Fire `event` with `{ seconds }` of dwell time when the user leaves the
 * mounting page. Two exit paths, mutually exclusive in practice:
 *
 *  - SPA navigation → the component unmounts, the effect cleanup fires.
 *  - Tab close / hard navigation → React never unmounts, so `pagehide` is the
 *    only signal; `sendBeacon` transport survives the page teardown.
 *
 * After a `pagehide` the clock restarts, so a BFCache restore followed by an
 * SPA navigation reports the second visit's dwell — not a double count.
 * No-op (via `trackEvent`) without a Mixpanel token or outside a chief session.
 */
export function useDwellTracking(event: AnalyticsEventName): void {
  useEffect(() => {
    let start = Date.now();
    const seconds = () => Math.round((Date.now() - start) / 1000);
    const onPageHide = () => {
      trackEvent(event, { seconds: seconds() }, { transport: "sendBeacon" });
      start = Date.now();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      trackEvent(event, { seconds: seconds() });
    };
  }, [event]);
}
